import { Box } from '@mui/material';
import { ReactNode, RefObject } from 'react';
import { LISTBOX_DOM_ID } from '../globalSearch.constants';
import { SearchableEntityType } from '../globalSearch.types';
import { getOptionDomId } from '../hooks/useGlobalSearchKeyboardNav';
import CommandRow from '../staticCommands/components/CommandRow/CommandRow';
import { GlobalSearchListItem } from '../staticCommands/staticCommands.types';
import ResultRow from './ResultRow/ResultRow';
import { GlobalSearchItemActions } from './ResultRow/resultRow.types';

const DIMMED_OPACITY = 0.8;

interface KeyboardNavigationConfig {
  isActive?: boolean;
  onMouseMove?: () => void;
}

interface ResultsListProps {
  items: GlobalSearchListItem[];
  query: string;
  onSelectItem: (item: GlobalSearchListItem) => void;
  ariaLabel: string;
  isDimmed?: boolean;
  activeIndex?: number;
  keyboardNavigation?: KeyboardNavigationConfig;
  scrollRef?: RefObject<HTMLDivElement | null>;
  footer?: ReactNode;
  itemActions?: GlobalSearchItemActions;
  canOpenEntity: (entityType: SearchableEntityType) => boolean;
}

function ResultsList({
  items,
  query,
  onSelectItem,
  ariaLabel,
  isDimmed,
  activeIndex = -1,
  keyboardNavigation,
  scrollRef,
  footer,
  itemActions,
  canOpenEntity,
}: ResultsListProps) {
  return (
    <Box
      ref={scrollRef}
      id={LISTBOX_DOM_ID}
      role="listbox"
      aria-label={ariaLabel}
      data-keyboard-nav={keyboardNavigation?.isActive ? 'true' : undefined}
      onMouseMove={keyboardNavigation?.onMouseMove}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        opacity: isDimmed ? DIMMED_OPACITY : 1,
        transition: (theme) => theme.transitions.create('opacity'),
      }}
    >
      {items.map((listItem, index) =>
        listItem.kind === 'command' ? (
          <CommandRow
            key={`command-${listItem.command.id}`}
            command={listItem.command}
            query={query}
            domId={getOptionDomId(index)}
            isActive={index === activeIndex}
            onSelect={() => onSelectItem(listItem)}
          />
        ) : (
          <ResultRow
            key={`entity-${listItem.item.entityType}-${listItem.item.id}`}
            item={listItem.item}
            query={query}
            domId={getOptionDomId(index)}
            isActive={index === activeIndex}
            isDisabled={!canOpenEntity(listItem.item.entityType)}
            itemActions={itemActions}
            onSelect={() => onSelectItem(listItem)}
          />
        ),
      )}
      {footer}
    </Box>
  );
}

export default ResultsList;
